import {
  type OFFER_CARGO_PROCESS_RECEPTION_STATUS,
  OfferCargoProcessReceptionStatusLabels,
} from "@/features/offer-cargo-process-status/badge-status";
import { fromIso8601Date, toLocaleTextAsDate } from "@/shared/lib/datetime";
import { SearchParamChipList } from "@/widgets/search-param-chip-list";
import type React from "react";
import { useMemo } from "react";
import type { AdminCargoListPageProps } from "../model";

export type OfferCargoSearchParamChipList_ParamKeys =
  | "loadingOn"
  | "loadingPrefecture"
  | "loadingAddress"
  | "unloadingOn"
  | "unloadingPrefecture"
  | "unloadingAddress"
  | "companyName"
  | "receptionStatus"
  | "contractStatus";

export type OfferCargoSearchParamChipListProps = {
  getDraftOffersCargosParams: AdminCargoListPageProps["getDraftOffersCargosParams"];
  receptionStatusFilter: OFFER_CARGO_PROCESS_RECEPTION_STATUS | undefined;
  companyNameFilter: string;
  onDelete: (values: OfferCargoSearchParamChipList_ParamKeys) => void;
};

export const OfferCargoSearchParamChipList: React.FC<
  OfferCargoSearchParamChipListProps
> = (props) => {
  const params = useMemo(() => {
    const result: {
      value: OfferCargoSearchParamChipList_ParamKeys;
      label: string;
    }[] = [];
    const { loadingOnFrom, loadingOnTo, loadingPrefecture, loadingAddress } =
      props.getDraftOffersCargosParams ?? {};
    if (loadingOnFrom || loadingOnTo) {
      const loadingOnFromText = loadingOnFrom
        ? toLocaleTextAsDate(fromIso8601Date(loadingOnFrom))
        : "";
      const loadingOnToText = loadingOnTo
        ? toLocaleTextAsDate(fromIso8601Date(loadingOnTo))
        : "";
      result.push({
        value: "loadingOn",
        label: `積日：${loadingOnFromText}〜${loadingOnToText}`,
      });
    }
    if (loadingPrefecture) {
      result.push({
        value: "loadingPrefecture",
        label: `積地(都道府県)：${loadingPrefecture}`,
      });
    }
    if (loadingAddress) {
      result.push({
        value: "loadingAddress",
        label: `積地(市区町村)：${loadingAddress}`,
      });
    }
    const {
      unloadingOnFrom,
      unloadingOnTo,
      unloadingPrefecture,
      unloadingAddress,
    } = props.getDraftOffersCargosParams ?? {};
    const companyName = props.companyNameFilter;
    if (unloadingOnFrom || unloadingOnTo) {
      const unloadingOnFromText = unloadingOnFrom
        ? toLocaleTextAsDate(fromIso8601Date(unloadingOnFrom))
        : "";
      const unloadingOnToText = unloadingOnTo
        ? toLocaleTextAsDate(fromIso8601Date(unloadingOnTo))
        : "";
      result.push({
        value: "unloadingOn",
        label: `卸日：${unloadingOnFromText}〜${unloadingOnToText}`,
      });
    }
    if (unloadingPrefecture) {
      result.push({
        value: "unloadingPrefecture",
        label: `卸地(都道府県)：${unloadingPrefecture}`,
      });
    }
    if (unloadingAddress) {
      result.push({
        value: "unloadingAddress",
        label: `卸地(市区町村)：${unloadingAddress}`,
      });
    }
    if (companyName) {
      result.push({
        value: "companyName",
        label: `荷主名：${companyName}`,
      });
    }
    if (props.receptionStatusFilter) {
      result.push({
        value: "receptionStatus",
        label: `受付状況：${OfferCargoProcessReceptionStatusLabels.find((v) => v.status === props.receptionStatusFilter) ?? ""}`,
      });
    }
    return result;
  }, [
    props.getDraftOffersCargosParams,
    props.receptionStatusFilter,
    props.companyNameFilter,
  ]);
  if (params.length === 0) {
    return null;
  }
  const handleOnDelete = (value: OfferCargoSearchParamChipList_ParamKeys) => {
    props.onDelete(value);
  };
  return <SearchParamChipList params={params} onDelete={handleOnDelete} />;
};
